
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container, Box, Typography, Paper,
    TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
    TextField, Button, IconButton, InputAdornment, MenuItem, Select, FormControl, InputLabel,
    CircularProgress
} from '@mui/material';
import {
    Download, Search, ExpandMore,
    AttachMoney as DollarSignIcon,
    CalendarMonth as CalendarIcon,
    AccessTime as ClockIcon,
    Business as Building2Icon
} from '@mui/icons-material';
import apiEndPointsConfig from '../../../services/apiEndPoints';
import axiosInstance from '../../../services/axiosInterceptors';
import { generateInvoicePDF } from '../../../utils/common';

// Generate month and year options
const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
];

// Generate years (current year and 5 years back)
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

// Helper function to simulate a download action
const handleDownload = (invoiceId) => {
    console.log(`Downloading invoice ID: ${invoiceId}`);
    // In a real application, implement actual download logic here
    console.log(`[UI Notification Placeholder]: Invoice ${invoiceId} download initiated!`);
};

// Component for consistently styled table headers
const TableHeaderCell = ({ icon: Icon, label }) => (
    <TableCell sx={{
        fontWeight: 'bold',
        color: 'text.secondary',
        backgroundColor: '#f9fafb',
        textTransform: 'uppercase',
        fontSize: 12
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {Icon && <Icon sx={{ fontSize: 16, mr: 0.5 }} />}
            {label}
        </Box>
    </TableCell>
);

const Invoices = () => {
    // Get current month and year as defaults
    const token = localStorage.getItem("token");
    const currentDate = new Date();
    const defaultMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
    const defaultYear = currentDate.getFullYear();

    const [invoices, setInvoices] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
    const [selectedYear, setSelectedYear] = useState(defaultYear);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [companyList, setCompanyList] = useState([]);


    useEffect(() => {
        const fetchCompanyList = async () => {
            try {
                const res = await axiosInstance.get(apiEndPointsConfig.companyList, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const list = res?.data?.data;
                console.log(list, "list");

                // list = { "Technotask": "67e560...", "Triotech": "67e56..." }

                const formatted = Object.entries(list).map(([name, id]) => ({
                    label: name,
                    value: id,
                }));

                setCompanyList(formatted);
            } catch (err) {
                console.error("Company list error:", err);
            }
        };

        fetchCompanyList();
    }, []);

    const downloadInvoicePdf = async (invoice) => {
        try {

            console.log(invoice , "invoice");
            
            const payload = {
                companyId:invoice.id,
                companyName: invoice.company,
                month: invoice.month,
                // year: invoice.id
            };

            const response = await axiosInstance.post(
                apiEndPointsConfig.downloadInvoice,
                payload,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    // responseType: "blob"
                }
            );
            console.log(response.data.data, "response.data.data");
            if (response.data?.data) {
                await generateInvoicePDF(response.data.data);
            }

        } catch (err) {
            console.error("Invoice download error:", err);
        }
    };

    const getCompanyNameById = (companyId) => {
        const company = companyList.find(c => c.value === companyId);
        return company ? company.label : companyId; // fallback
    };

    // Fetch invoices from API
    const fetchInvoices = async () => {
        setLoading(true);
        setError(null);

        const payload = {
            month: selectedMonth,
            year: String(selectedYear)
        }
        try {
            const response = await axios.post(apiEndPointsConfig.invoiceGeneration, payload, { headers: { Authorization: `Bearer ${token}` } });

            console.log(response, "response");


            if (response.data.status.code === 200 && response.data.data?.companies) {
                // Transform API data to match component structure
                const transformedData = response.data.data.companies.map((company) => ({
                    id: company.companyId,
                    company: getCompanyNameById(company.companyId), // You might want to map this to actual company names
                    month: `${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}`,
                    totalMinutes: company.totalMinutes,
                    totalCost: company.totalCost,
                    generationDate: new Date(company.invoiceGenerationDate).toISOString().split('T')[0],
                    totalCalls: company.totalCalls,
                    dueAmount: company.dueAmount
                }));
                setInvoices(transformedData);
                console.log(invoices, "totalCost");

            } else {
                setError('Failed to fetch invoices');
            }
        } catch (err) {
            console.error('Error fetching invoices:', err);
            setError(err.message || 'An error occurred while fetching invoices');
        } finally {
            setLoading(false);
        }
    };

    // Fetch invoices on component mount and when month/year changes
    useEffect(() => {
        fetchInvoices();
    }, [selectedMonth, selectedYear, companyList]);

    // Filter invoices based on search term (company name or month)
    const filteredInvoices = invoices.filter(invoice =>
        invoice.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.month.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#ffff', p: 3 }}>

            {/* Header and Title - Commented out as per original
      <Paper elevation={3} sx={{ mb: 4, p: 3, borderRadius: '12px' }}>
        <Box display="flex" alignItems="center">
          <DollarSignIcon sx={{ width: 32, height: 32, mr: 2, p: 1, bgcolor: 'primary.light', color: 'primary.main', borderRadius: '50%' }} />
          <Typography variant="h4" component="h1" fontWeight="extrabold" color="primary.dark">
            Super Admin Billing & Invoices
          </Typography>
        </Box>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
          Overview of all client usage, costs, and generated invoice documents.
        </Typography>
      </Paper> */}

            {/* Control Panel: Search and Filters */}
            <Box
                display="flex"
                flexDirection={{ xs: 'column', md: 'row' }}
                gap={2}
                mb={3}
            >
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search by Company or Month..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search color="action" />
                            </InputAdornment>
                        ),
                        sx: {
                            borderRadius: '8px',
                            backgroundColor: 'white',
                        }
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />

                <FormControl sx={{ minWidth: { xs: '100%', md: 150 }, bgcolor: 'white', borderRadius: '8px' }}>
                    <InputLabel>Month</InputLabel>
                    <Select
                        value={selectedMonth}
                        label="Month"
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        sx={{ borderRadius: '8px' }}
                    >
                        {months.map((month) => (
                            <MenuItem key={month.value} value={month.value}>
                                {month.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: { xs: '100%', md: 120 }, bgcolor: 'white', borderRadius: '8px' }}>
                    <InputLabel>Year</InputLabel>
                    <Select
                        value={selectedYear}
                        label="Year"
                        onChange={(e) => setSelectedYear(e.target.value)}
                        sx={{ borderRadius: '8px' }}
                    >
                        {years.map((year) => (
                            <MenuItem key={year} value={year}>
                                {year}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Invoice Table Container */}
            <Paper elevation={3} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, p: 3 }}>
                        <Typography color="error" variant="h6">{error}</Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table stickyHeader aria-label="invoice table">
                            <TableHead>
                                <TableRow>
                                    <TableHeaderCell icon={Building2Icon} label="Company Name" />
                                    <TableHeaderCell icon={CalendarIcon} label="Month" />
                                    <TableHeaderCell icon={ClockIcon} label="Total Minutes" />
                                    <TableHeaderCell icon={DollarSignIcon} label="Total Cost" />
                                    <TableHeaderCell icon={CalendarIcon} label="Generation Date" />
                                    <TableHeaderCell label="Actions" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredInvoices.length > 0 ? (
                                    filteredInvoices.map((invoice) => (
                                        <TableRow
                                            key={invoice.id}
                                            hover
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell component="th" scope="row" sx={{ fontWeight: 'medium' }}>
                                                {invoice.company}
                                            </TableCell>
                                            <TableCell>{invoice.month}</TableCell>
                                            <TableCell>{invoice.totalMinutes.toLocaleString()}</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                                ${invoice.totalCost.toFixed(2)}
                                            </TableCell>
                                            <TableCell>{invoice.generationDate}</TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    onClick={() => downloadInvoicePdf(invoice)}
                                                    aria-label={`download invoice ${invoice.id}`}
                                                    size="small"
                                                    color="primary"
                                                    sx={{ bgcolor: '#8a2ef5', color: "white", '&:hover': { bgcolor: '#8a2ef5', color: 'white' } }}
                                                >
                                                    <Download fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                                            <Typography variant="h6">No invoices match your search criteria.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Footer / Pagination Placeholder */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="body2" color="text.secondary">
                    Showing 1 to {filteredInvoices.length} of {invoices.length} results
                </Typography>
            </Box>

        </Box>
    );
};

export default Invoices;